import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersCompanyStatusFormData,
  ParametersCompanyStatusSchema,
} from "@/types/forms/bo/clientsParameters.form";
import { CompanyStatusEnum } from "@/constants/enums/companyStatus.enum";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { useCompanyStatusOptions } from "@/hooks/useEnumOptions";

type UpdateStatusModalProps = {
  closeModal: () => void;
  status: CompanyStatusEnum;
  title: string;
};

export const UpdateStatusModal = ({ closeModal, status, title }: UpdateStatusModalProps) => {
  const companyStatusOptions = useCompanyStatusOptions();
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const statusForm = useForm<ParametersCompanyStatusFormData>({
    resolver: zodResolver(ParametersCompanyStatusSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      status,
    },
  });

  const onSubmit: SubmitHandler<ParametersCompanyStatusFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  const statusDefaultValue = companyStatusOptions.find((s) => s.value === status)?.value;

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="statusForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("statusModal.submit")}
        </Button>
      }
    >
      <form
        id="statusForm"
        onSubmit={statusForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledSelectV2
          options={companyStatusOptions}
          control={statusForm.control}
          name="status"
          wrapperClassName="w-full"
          defaultValue={statusDefaultValue}
        />
      </form>
    </Modal>
  );
};
